import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { BehaviorSubject, Observable, catchError, filter, map, take, tap, throwError } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  AuthSession,
  SendOtpRequest,
  VerifyOtpRequest,
  VerifyOtpResponse,
} from '../models/auth.model';

const TOKEN_KEY = 'chambia_token';
const REFRESH_TOKEN_KEY = 'chambia_refresh_token';
const USER_KEY = 'chambia_user';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  readonly session = signal<AuthSession | null>(this.loadSession());

  private isRefreshing = false;
  private refreshedToken$ = new BehaviorSubject<string | null>(null);

  get isLoggedIn(): boolean {
    return !!this.session();
  }

  sendOtp(phoneNumber: string): Observable<void> {
    const body: SendOtpRequest = { phoneNumber };
    return this.http.post<void>(`${environment.apiBaseUrl}/auth/send-otp`, body);
  }

  verifyOtp(phoneNumber: string, code: string): Observable<VerifyOtpResponse> {
    const body: VerifyOtpRequest = { phoneNumber, code };
    return this.http
      .post<VerifyOtpResponse>(`${environment.apiBaseUrl}/auth/verify-otp`, body)
      .pipe(tap((res) => this.persistSession({ token: res.token, refreshToken: res.refreshToken, userId: res.userId, role: res.role })));
  }

  /**
   * Renueva el access token con el refresh token (7 días) — invocado por
   * refreshTokenInterceptor cuando una petición autenticada falla con 401.
   * Solo aplica en el navegador: durante SSR nunca hay sesión cargada.
   */
  refreshAccessToken(): Observable<string> {
    const current = this.session();
    if (!this.isBrowser || !current?.refreshToken) {
      this.logout();
      return throwError(() => new Error('No hay sesión activa para refrescar'));
    }

    if (this.isRefreshing) {
      return this.refreshedToken$.pipe(
        filter((t): t is string => t !== null),
        take(1)
      );
    }

    this.isRefreshing = true;
    this.refreshedToken$.next(null);

    return this.http
      .post<VerifyOtpResponse>(`${environment.apiBaseUrl}/auth/refresh`, { refreshToken: current.refreshToken })
      .pipe(
        tap((res) => {
          this.persistSession({ token: res.token, refreshToken: res.refreshToken, userId: current.userId, role: current.role });
          this.isRefreshing = false;
          this.refreshedToken$.next(res.token);
        }),
        map((res) => res.token),
        catchError((err) => {
          this.isRefreshing = false;
          this.logout();
          return throwError(() => err);
        })
      );
  }

  logout(): void {
    const refreshToken = this.session()?.refreshToken;
    this.session.set(null);
    if (this.isBrowser) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      this.http.post(`${environment.apiBaseUrl}/auth/logout`, { refreshToken }).subscribe({ error: () => void 0 });
    }
  }

  getToken(): string | null {
    return this.session()?.token ?? null;
  }

  private persistSession(session: AuthSession): void {
    this.session.set(session);
    if (this.isBrowser) {
      localStorage.setItem(TOKEN_KEY, session.token);
      localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
      localStorage.setItem(USER_KEY, JSON.stringify({ userId: session.userId, role: session.role }));
    }
  }

  private loadSession(): AuthSession | null {
    if (!this.isBrowser) return null;
    const token = localStorage.getItem(TOKEN_KEY);
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    const userRaw = localStorage.getItem(USER_KEY);
    if (!token || !refreshToken || !userRaw) return null;
    try {
      const user: { userId: string; role: string } = JSON.parse(userRaw);
      return { token, refreshToken, userId: user.userId, role: user.role };
    } catch {
      return null;
    }
  }
}
